<!-- Botón en la barra superior -->
<button
  type="button"
  (click)="show('')"
  aria-label="Buscar en el menú"
  aria-haspopup="dialog"
  class="group dark:bg-secondary-800/70 dark:border-secondary-500 dark:hover:bg-secondary-700 focus-visible:ring-primary-500/40 flex h-9 cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white/70 pr-1.5 pl-3 text-sm text-gray-500 shadow-sm backdrop-blur transition-all duration-200 ease-out hover:border-gray-300 hover:bg-white hover:text-gray-800 hover:shadow-md focus-visible:ring-2 focus-visible:outline-none dark:text-gray-400 dark:hover:text-gray-100">
  <ng-icon
    name="matSearch"
    size="18"
    class="group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200" />
  <span class="hidden pr-6 md:inline">Buscar…</span>
  <span class="hidden items-center gap-0.5 md:flex">
    <kbd class="gs-kbd">{{ modifierKey }}</kbd>
    <kbd class="gs-kbd">K</kbd>
  </span>
</button>

@if (open()) {
  <div
    class="fixed inset-0 z-[1100] flex items-start justify-center px-4 pt-[10vh] md:pt-[14vh]"
    animate.enter="gs-enter"
    animate.leave="gs-leave">
    <!-- Fondo: click fuera cierra (con teclado se cierra con Esc) -->
    <button
      type="button"
      tabindex="-1"
      aria-label="Cerrar buscador"
      class="gs-backdrop absolute inset-0 cursor-default bg-gray-900/40 backdrop-blur-sm dark:bg-black/60"
      (click)="close()"></button>

    <div
      role="dialog"
      aria-modal="true"
      aria-label="Buscador del menú"
      class="gs-panel dark:bg-secondary-900 dark:ring-secondary-500/60 relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/10 dark:shadow-black/60">
      <!-- Brillo de marca detrás del input -->
      <div
        aria-hidden="true"
        class="bg-primary-500/15 dark:bg-primary-500/20 pointer-events-none absolute -top-24 left-1/2 h-48 w-[70%] -translate-x-1/2 rounded-full blur-3xl"></div>

      <!-- Input -->
      <div
        class="dark:border-secondary-700 relative flex items-center gap-3 border-b border-gray-100 px-5 py-4">
        <ng-icon
          name="matSearch"
          size="22"
          class="shrink-0 transition-colors duration-200"
          [class]="
            query() ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'
          " />
        <input
          #searchInput
          type="text"
          [value]="query()"
          (input)="onQueryChange(searchInput.value)"
          (keydown)="onInputKeydown($event)"
          placeholder="¿A qué sección quieres ir?"
          aria-label="Buscar sección del menú"
          autocomplete="off"
          spellcheck="false"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="global-search-results"
          aria-expanded="true"
          [attr.aria-activedescendant]="
            results().length ? 'global-search-item-' + activeIndex() : null
          "
          class="caret-primary-600 w-full bg-transparent text-lg font-medium text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400 dark:text-gray-50" />
        @if (query()) {
          <button
            type="button"
            (click)="clear()"
            aria-label="Limpiar búsqueda"
            class="dark:hover:bg-secondary-700 focus-visible:ring-primary-500/40 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-2 focus-visible:outline-none dark:hover:text-gray-200">
            <ng-icon name="matClose" size="18" />
          </button>
        }
      </div>

      <!-- Resultados -->
      <ul
        #resultsList
        id="global-search-results"
        role="listbox"
        aria-label="Secciones del menú"
        class="relative max-h-[min(55vh,26rem)] overflow-y-auto overscroll-contain p-2">
        @for (item of results(); track item.path; let i = $index) {
          @let active = i === activeIndex();
          <li
            role="option"
            [id]="'global-search-item-' + i"
            [attr.data-index]="i"
            [attr.aria-selected]="active"
            tabindex="-1"
            (click)="go(item)"
            (keydown.enter)="go(item)"
            (mousemove)="activeIndex.set(i)"
            class="relative flex min-h-12 cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors duration-150"
            [class]="
              active
                ? 'bg-primary-50 dark:bg-primary-500/10'
                : 'dark:hover:bg-secondary-800/60 hover:bg-gray-50'
            ">
            <!-- Marca lateral del activo -->
            <span
              aria-hidden="true"
              class="bg-primary-600 dark:bg-primary-400 absolute top-2.5 bottom-2.5 left-0 w-1 rounded-full transition-all duration-200 ease-out"
              [class]="
                active ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
              "></span>

            <span
              class="flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-150"
              [class]="
                active
                  ? 'bg-primary-600 shadow-primary-600/30 dark:bg-primary-500 text-white shadow-sm'
                  : 'dark:bg-secondary-800 bg-gray-100 text-gray-500 dark:text-gray-400'
              ">
              <ng-icon [name]="item.icon" size="18" />
            </span>

            <span class="flex min-w-0 flex-1 flex-col">
              <span
                class="truncate text-sm font-medium"
                [class]="
                  active
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-700 dark:text-gray-200'
                ">
                @for (seg of item.segments; track $index) {
                  @if (seg.match) {
                    <mark
                      class="text-primary-700 dark:text-primary-300 bg-transparent font-bold"
                      >{{ seg.text }}</mark
                    >
                  } @else {
                    {{ seg.text }}
                  }
                }
              </span>
              <span class="truncate text-xs opacity-50">
                @if (item.parent) {
                  {{ item.parent }} ·
                }
                {{ item.path }}
              </span>
            </span>

            <span
              aria-hidden="true"
              class="text-primary-600 dark:text-primary-400 hidden shrink-0 items-center gap-1 text-xs font-semibold transition-all duration-200 ease-out sm:flex"
              [class]="
                active ? 'translate-x-0 opacity-100' : '-translate-x-1 opacity-0'
              ">
              Abrir
              <ng-icon name="matKeyboardReturn" size="16" />
            </span>
          </li>
        } @empty {
          <li
            role="presentation"
            class="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <span
              class="dark:bg-secondary-800 flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
              <ng-icon name="matSearchOff" size="24" />
            </span>
            <span class="flex flex-col gap-1">
              <span class="text-sm font-bold text-gray-800 dark:text-gray-100">
                Nada coincide con "{{ query() }}"
              </span>
              <span class="text-sm opacity-60">
                Prueba con el nombre de la sección o el de su menú.
              </span>
            </span>
            <button
              type="button"
              (click)="clear()"
              class="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 focus-visible:ring-primary-500/40 cursor-pointer rounded-full px-3 py-1.5 text-sm font-semibold transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none">
              Ver todas las secciones
            </button>
          </li>
        }
      </ul>

      <!-- Pie con atajos -->
      <div
        class="dark:border-secondary-700 dark:bg-secondary-950/40 flex items-center justify-between gap-4 border-t border-gray-100 bg-gray-50/80 px-5 py-2.5 text-xs text-gray-500 dark:text-gray-400">
        <div class="flex items-center gap-4">
          <span class="flex items-center gap-1.5">
            <kbd class="gs-kbd">↑</kbd><kbd class="gs-kbd">↓</kbd>
            <span class="hidden sm:inline">navegar</span>
          </span>
          <span class="flex items-center gap-1.5">
            <kbd class="gs-kbd">↵</kbd>
            <span class="hidden sm:inline">abrir</span>
          </span>
          <span class="flex items-center gap-1.5">
            <kbd class="gs-kbd">Esc</kbd>
            <span class="hidden sm:inline">cerrar</span>
          </span>
        </div>
        <span aria-live="polite" class="tabular-nums">
          {{ results().length }}
          {{ results().length === 1 ? 'sección' : 'secciones' }}
        </span>
      </div>
    </div>
  </div>
}
